import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';

import { ParameterField } from '../../../../_common/platform/tsx/ParameterField';
import TreeOptions from '../../../../_common/platform/tsx/filtering/TreeOptions';
import SelectField, { createSelectFieldOptions } from '../../../../_common/platform/tsx/SelectField';
import BooleanField from '../../../../_common/platform/tsx/BooleanField';
import TimePartOptions from '../../../../_common/platform/tsx/filtering/TimePartOptions';

import {
    filterHasControlType,
    getValidControlTypes
} from '../../../../_common/platform/tsx/filtering/AttrQueryFilterField';

import * as Utilities from '../../../../_common/platform/ts/Utilities';
import * as FilterUtilities from '../../../platform/tsx/filtering/FilterUtilities';
import { IValuesCollection } from '../../../platform/ts/appTheme';

import {
    IQueryFilter,
    IAttributeTreeProperties,
    QueryFilterValueType,
    IAttributeTimePartOptions
} from '../../../../_common/platform/tsx/filtering/Interfaces';

import { ATTRIBUTE_CONTROL_TYPES } from '../../../platform/ts/ConfigurationConstants';

import { ISize } from '../../ts/Interfaces';

require('../../../../_common/platform/sass/attribute-options-select.sass');

export interface IProperties {
    value: IQueryFilter;
    dataSourceID: number;
    theme: IValuesCollection;
    glob: IGlob;

    onChange(value: IQueryFilter): void;
}

interface IPopupOptions {
    value: IQueryFilter;
    dataSourceID: number;
    theme: IValuesCollection;
    zIndex?: number;

    offset: {
        left: number;
        top: number;
    };

    onChange(value: IQueryFilter): void;

    component: typeof OptionsSelectPopup;
}

interface IOptionsSelectPopupProps {
    zIndex: number;
    options: IPopupOptions;
    glob: IGlob;

    close(): void;
}

function getAttributeType(value: IQueryFilter, props: IOptionsSelectPopupProps) {
    return props.glob.EntityModelsStore.getAttributeType(value, props.options.dataSourceID);
}

function OptionsSelectPopup(props: IOptionsSelectPopupProps) {
    const popoverContent = React.useRef<HTMLDivElement>(null);

    const [size, setSize] = React.useState<ISize>({ width: 600, height: 400 });

    const [value, setValue] = React.useState<IQueryFilter>(() => {
        const value = $.extend(true, {}, props.options.value);

        if (value.options == null) {
            value.options = {};
        }

        if (value.options.controlType == null) {
            const attributeType = getAttributeType(value, props);
            const validTypes = getValidControlTypes(
                value,
                attributeType,
                value.relation,
                props.options.dataSourceID,
                props.glob
            );

            if (validTypes != null && validTypes.length > 0) {
                value.options.controlType = validTypes[0].type;
            }
        }

        return value;
    });

    React.useEffect(() => {
        const dialog = popoverContent.current;

        setSize({
            height: dialog.clientHeight,
            width: dialog.clientWidth
        });
    }, []);

    const handleOKButtonClick = () => {
        props.close();

        if (typeof props.options.onChange === 'function') {
            const value = getValueCopy();

            props.options.onChange(value);
        }
    };

    const handleContainerClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const getValueCopy = () => {
        return $.extend(true, {}, value);
    };

    const handleTreeOptionsChange = (value: IAttributeTreeProperties) => {
        const result = getValueCopy();

        result.options.parentAttribute = value.parentAttribute;

        setValue(result);
    };

    const handleControlTypeChange = (value: string) => {
        const result = getValueCopy();

        result.options.controlType = value;

        FilterUtilities.setIntervalsByControlType(
            result,
            result.attribute,
            result.valueType,
            value,
            props.options.dataSourceID,
            props.glob
        );

        setValue(result);
    };

    const handleOnDeepFilteringChange = (value: boolean) => {
        const result = getValueCopy();

        if (value) {
            //Default value is true.
            delete result.options.deepFiltering;
        } else {
            result.options.deepFiltering = value;
        }

        setValue(result);
    };

    const handleValueTypeChange = (value: string) => {
        const result = getValueCopy();
        const valueType = value as QueryFilterValueType;

        if (valueType !== result.valueType) {
            result.valueType = valueType;
            result.value = null;

            setValue(result);
        }
    };

    const getDeepFilteringControl = (value: IQueryFilter) => {
        return (
            <ParameterField key="deep-filtering" noTitle={true}>
                <BooleanField
                    value={value.options.deepFiltering !== false}
                    allowNull={false}
                    title={'Deep Filtering'}
                    onChange={handleOnDeepFilteringChange}
                />
            </ParameterField>
        );
    };

    const getValueTypeSelector = (value: IQueryFilter) => {
        if (value == null || (value.attribute == null && value.expression == null)) {
            return;
        }

        const attributeType = getAttributeType(value, props);

        if (FilterUtilities.filterValueTypeCannotBeChanged(attributeType, value.relation)) {
            return;
        }

        const valueType = FilterUtilities.getValueType(value);

        return (
            <ParameterField key="value-type" title={'Value Type'}>
                <SelectField value={valueType} onChange={handleValueTypeChange}>
                    <option value={'constant'}>{'Constant'}</option>
                    <option value={'variable'}>{'Variable'}</option>
                    <option value={'exchangeObject'}>{'Exchange Object'}</option>
                </SelectField>
            </ParameterField>
        );
    };

    const getControlTypeControl = (value: IQueryFilter) => {
        if (!filterHasControlType(value)) {
            return null;
        }

        const attributeType = getAttributeType(value, props);
        const validTypes = getValidControlTypes(
            value,
            attributeType,
            value.relation,
            props.options.dataSourceID,
            props.glob
        );
        const selectOptions = createSelectFieldOptions(validTypes);

        if (selectOptions != null && selectOptions.length > 0) {
            return (
                <ParameterField key="control-type" title={'Display'}>
                    <SelectField value={value.options.controlType} onChange={handleControlTypeChange}>
                        {selectOptions}
                    </SelectField>
                </ParameterField>
            );
        }

        return null;
    };

    const getTreeOptions = (value: IQueryFilter) => {
        const { relation, attribute, options, valueType } = value;

        if (
            valueType === 'variable' ||
            (!FilterUtilities.isIsSubnodeOfRelation(relation) && options.controlType !== ATTRIBUTE_CONTROL_TYPES.TREE)
        ) {
            return null;
        }

        return (
            <TreeOptions
                key="treeOptions"
                value={options}
                attribute={attribute}
                dataSourceID={props.options.dataSourceID}
                hideOptionalFields={true}
                theme={props.options.theme}
                glob={props.glob}
                onChange={handleTreeOptionsChange}
            />
        );
    };

    const handleOnTimePartOptionsChange = (value: IAttributeTimePartOptions) => {
        const result = getValueCopy();

        result.options = value;

        setValue(result);
    };

    const handleTimePartStartMonthChange = (value: number) => {
        const result = getValueCopy();

        if (value != null) {
            result.intervals.timePeriodStartMonth = value;
        } else {
            delete result.intervals.timePeriodStartMonth;
        }

        setValue(result);
    };

    const getTimePartOptions = (value: IQueryFilter) => {
        const { intervals } = value;

        if (intervals == null) {
            return null;
        }

        return (
            <TimePartOptions
                value={value.options}
                intervals={intervals}
                theme={props.options.theme}
                hideTimePartControlType={true}
                hideYearOptions={value.valueType === 'variable'}
                onChange={handleOnTimePartOptionsChange}
                onTimePartStartMonthChange={handleTimePartStartMonthChange}
            />
        );
    };

    const options = props.options;
    const popoverStyle = Utilities.getPopoverStyle(options.offset, size);
    const zIndex = options.zIndex != null ? options.zIndex : props.zIndex;
    const style = {
        display: 'block',
        top: popoverStyle.top,
        left: popoverStyle.left,
        zIndex: zIndex
    };

    return (
        <div className="attribute-options-select-dialog">
            <div
                className={'popover attribute-options-select-popover ' + popoverStyle.side}
                style={style}
                ref={popoverContent}
                onClick={handleContainerClick}
            >
                <div className="arrow" style={{ top: popoverStyle.arrowTop }}></div>

                <h3 className="popover-header">
                    {'Options'}

                    <button type="button" className="close" onClick={props.close}>
                        ×
                    </button>
                </h3>

                <div className="popover-body">
                    {getDeepFilteringControl(value)}

                    {getValueTypeSelector(value)}

                    {getControlTypeControl(value)}

                    {getTreeOptions(value)}

                    {getTimePartOptions(value)}
                </div>

                <div className="modal-footer">
                    <div className="btn btn-success button-confirm" onClick={handleOKButtonClick}>
                        {'OK'}
                    </div>

                    <button className="btn btn-default" onClick={props.close}>
                        {'Cancel'}
                    </button>
                </div>
            </div>

            <div className="modal-backdrop fade show"></div>
        </div>
    );
}

export default function AttrQueryFilterFieldOptionsSelector(props: IProperties) {
    const handleOnChange = (value: IQueryFilter) => {
        if (typeof props.onChange === 'function') {
            props.onChange(value);
        }
    };

    const handleOnSelectorShow = (e) => {
        props.glob.DialogActions.createPopup<IPopupOptions>('custom', {
            offset: $(e.target).offset(),

            value: props.value,
            dataSourceID: props.dataSourceID,
            theme: props.theme,

            onChange: handleOnChange,

            component: OptionsSelectPopup
        });
    };

    let variableMark: React.JSX.Element;

    if (!_.isEmpty(props.value)) {
        variableMark = <span className="glyphicon glyphicon-equalizer" onClick={handleOnSelectorShow}></span>;
    }

    return (
        <div className="attribute-options-select">
            <div className="content">
                <span className="glyphicon glyphicon-cog" onClick={handleOnSelectorShow}></span>

                <span className="value" title={''}>
                    {variableMark}
                    <em className="empty" onClick={handleOnSelectorShow}></em>
                </span>
            </div>
        </div>
    );
}
